import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { useShop, useTokens } from "../lib/hooks";
import BrandHeader from "../components/BrandHeader";
import { FullPageLoader, ShopMissing } from "../components/States";
import { Clock, Users, CheckCircle2, RadioTower, ArrowLeft, Download } from "lucide-react";

async function downloadTicketPng(shop, token) {
  const W = 1080;
  const H = 1620;
  const accent = shop.brand_color || "#E6C662";
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Helpers
  const fitText = (text, maxW, maxFont, font) => {
    let size = maxFont;
    while (size > 12) {
      ctx.font = font.replace("__SIZE__", size);
      if (ctx.measureText(text).width <= maxW) break;
      size -= 2;
    }
    return size;
  };

  // === BACKGROUND — premium deep gradient + faint grain ===
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0c0c0c");
  bg.addColorStop(0.5, "#070707");
  bg.addColorStop(1, "#020202");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Soft gold glow behind the token number
  const glow = ctx.createRadialGradient(W / 2, 720, 50, W / 2, 720, 520);
  glow.addColorStop(0, `${accent}33`);
  glow.addColorStop(1, "transparent");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // === FRAMES — outer gold, inner hairline ===
  ctx.strokeStyle = accent;
  ctx.lineWidth = 4;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  ctx.strokeStyle = "rgba(230,198,98,0.22)";
  ctx.lineWidth = 1;
  ctx.strokeRect(72, 72, W - 144, H - 144);

  ctx.textAlign = "center";

  // === HEADER eyebrow ===
  ctx.fillStyle = accent;
  ctx.font = "600 26px 'IBM Plex Mono', monospace";
  ctx.fillText("AIBOX  ·  DIGITAL  QUEUE  TICKET", W / 2, 150);

  // === SHOP NAME — auto-shrink to fit inside the inner frame width ===
  const shopMaxW = W - 220; // generous side padding
  const shopFontTpl = "italic 600 __SIZEpx 'Playfair Display', serif";
  const shopSize = fitText(shop.name || "—", shopMaxW, 78, shopFontTpl);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = shopFontTpl.replace("__SIZE", shopSize);
  ctx.fillText(shop.name || "—", W / 2, 248);

  // Decorative gold rule under shop name
  const ruleY = 282;
  ctx.strokeStyle = `${accent}66`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 90, ruleY);
  ctx.lineTo(W / 2 + 90, ruleY);
  ctx.stroke();
  // Tiny diamond at center
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(W / 2, ruleY - 5);
  ctx.lineTo(W / 2 + 5, ruleY);
  ctx.lineTo(W / 2, ruleY + 5);
  ctx.lineTo(W / 2 - 5, ruleY);
  ctx.closePath();
  ctx.fill();

  // === TOKEN NUMBER — the hero ===
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "400 22px 'IBM Plex Mono', monospace";
  ctx.fillText("TOKEN  NUMBER", W / 2, 380);

  // Token number with subtle shadow for depth
  const tokenText = String(token.token_number);
  // Shrink only if number is very long (e.g. 4+ digits)
  const tokenFontTpl = "600 __SIZEpx 'IBM Plex Mono', monospace";
  const tokenSize = fitText(tokenText, W - 280, 380, tokenFontTpl);
  ctx.font = tokenFontTpl.replace("__SIZE", tokenSize);
  ctx.shadowColor = `${accent}77`;
  ctx.shadowBlur = 40;
  ctx.fillStyle = accent;
  ctx.fillText(tokenText, W / 2, 700);
  ctx.shadowBlur = 0;

  // === CUSTOMER NAME — auto-shrink ===
  const nameMaxW = W - 220;
  const nameFontTpl = "italic 600 __SIZEpx 'Playfair Display', serif";
  const customer = token.customer_name || "—";
  const nameSize = fitText(customer, nameMaxW, 54, nameFontTpl);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = nameFontTpl.replace("__SIZE", nameSize);
  ctx.fillText(customer, W / 2, 850);

  // === SERVICE — auto-shrink, single-line clip ===
  const serviceMaxW = W - 260;
  const serviceFontTpl = "400 __SIZEpx 'Outfit', sans-serif";
  const service = token.service_name || "—";
  const serviceSize = fitText(service, serviceMaxW, 28, serviceFontTpl);
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.font = serviceFontTpl.replace("__SIZE", serviceSize);
  ctx.fillText(service, W / 2, 900);

  // === META ROW — WhatsApp + Date ===
  const dateStr = new Date(token.created_at || Date.now()).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = "400 20px 'IBM Plex Mono', monospace";
  ctx.fillText(`WHATSAPP  ${token.whatsapp || "—"}    ·    ${dateStr.toUpperCase()}`, W / 2, 962);

  // === QR CODE — premium framed card ===
  const ticketUrl = `${window.location.origin}/s/${shop.slug}/ticket/${token.token_number}`;
  const qrDataUrl = await QRCode.toDataURL(ticketUrl, {
    width: 360,
    margin: 1,
    color: { dark: "#FFFFFF", light: "#0A0A0A" },
    errorCorrectionLevel: "H",
  });
  const img = new Image();
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
    img.src = qrDataUrl;
  });
  const qrSize = 340;
  const qrX = (W - qrSize) / 2;
  const qrY = 1040;
  const pad = 26;
  const r = 22;
  const x = qrX - pad,
    y = qrY - pad,
    w = qrSize + pad * 2,
    h = qrSize + pad * 2;
  // Card background + gold border
  ctx.fillStyle = "#0A0A0A";
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "400 18px 'IBM Plex Mono', monospace";
  ctx.fillText("SCAN  ·  CHECK  LIVE  STATUS", W / 2, qrY + qrSize + 70);

  // === FOOTER ===
  ctx.fillStyle = `${accent}d0`;
  ctx.font = "600 20px 'IBM Plex Mono', monospace";
  ctx.fillText("AIBOX  GLOBAL  ·  AIBOXHUB.IN", W / 2, H - 110);
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.font = "400 16px 'IBM Plex Mono', monospace";
  ctx.fillText("Skip the wait — book online", W / 2, H - 82);

  // Trigger download
  canvas.toBlob((blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `aibox-${shop.slug}-token-${token.token_number}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }, "image/png");
}

export default function DigitalTicketPage() {
  const { slug, tokenNumber } = useParams();
  const { shop, loading: shopLoading } = useShop(slug);
  const { tokens, loading: tokensLoading } = useTokens(shop?.id);
  const [now, setNow] = useState(Date.now());

  // 1-second tick for live countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const view = useMemo(() => {
    if (!tokens?.length) return null;
    const me = tokens.find((t) => String(t.token_number) === String(tokenNumber));
    if (!me) return { me: null };
    const waitingList = tokens.filter((t) => t.status === "waiting");
    const idx = waitingList.findIndex((t) => t.id === me.id);
    const ahead = idx < 0 ? 0 : idx;
    const etaMinutes = ahead === 0 ? 0 : Math.max(5, ahead * 12);
    const nowServing = tokens.find((t) => t.status === "serving") || null;
    const waitingCount = waitingList.length;
    return { me, ahead, etaMinutes, nowServing, waitingCount };
  }, [tokens, tokenNumber]);

  if (shopLoading || tokensLoading) return <FullPageLoader label="Loading ticket" />;
  if (!shop) return <ShopMissing slug={slug} />;

  if (!view?.me)
    return (
      <div className="min-h-screen bg-void text-white">
        <BrandHeader shop={shop} />
        <div className="max-w-md mx-auto px-5 py-20 text-center">
          <div className="font-heading text-3xl mb-3">Ticket not found</div>
          <p className="text-white/50 mb-8">
            Token #{tokenNumber} doesn&apos;t exist for today.
          </p>
          <Link to={`/s/${slug}/book`} className="btn-gold inline-block">
            Book a new token
          </Link>
        </div>
      </div>
    );

  const { me, ahead, etaMinutes, nowServing, waitingCount } = view;
  const statusMap = {
    waiting: { label: "Waiting", tone: "text-white", dot: "bg-white" },
    serving: { label: "Now Serving", tone: "text-gold", dot: "bg-gold" },
    completed: { label: "Completed", tone: "text-emerald-400", dot: "bg-emerald-400" },
    skipped: { label: "Skipped", tone: "text-red-400", dot: "bg-red-400" },
  };
  const st = statusMap[me.status] || statusMap.waiting;

  // Live countdown — anchor target to (created_at + etaMinutes) so it shrinks every second
  const target = new Date(me.created_at).getTime() + etaMinutes * 60_000;
  const remainMs = Math.max(0, target - now);
  const mm = Math.floor(remainMs / 60_000);
  const ss = Math.floor((remainMs % 60_000) / 1000);

  function etaLabel() {
    if (me.status === "serving") return "Now";
    if (me.status === "completed") return "Done";
    if (ahead === 0) return "You're up";
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  function positionLabel() {
    if (me.status === "completed") return "—";
    return ahead + 1;
  }
  function positionHint() {
    if (ahead === 0 && me.status !== "completed") return "You're next";
    return `${ahead} ahead`;
  }

  return (
    <div className="min-h-screen bg-void text-white">
      <BrandHeader
        shop={shop}
        right={
          <Link
            to={`/s/${slug}/book`}
            className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1"
            data-testid="ticket-back"
          >
            <ArrowLeft className="w-4 h-4" /> Book again
          </Link>
        }
      />

      <main className="px-5 md:px-10 pb-16 max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4"
          data-testid="digital-ticket"
        >
          {/* Status pill */}
          <div className="flex items-center gap-2 text-sm">
            <span className={`w-2 h-2 rounded-full ${st.dot} ${me.status === "serving" ? "animate-gold-pulse" : ""}`} />
            <span className={`font-numbers tracking-[0.3em] uppercase text-xs ${st.tone}`}>
              {st.label}
            </span>
          </div>

          {/* LIVE QUEUE STRIP — updates every 5s + realtime */}
          <div
            className="mt-4 rounded-2xl border border-gold/20 bg-gradient-to-r from-gold/[0.06] to-transparent p-4 flex items-center justify-between gap-4"
            data-testid="live-queue-strip"
          >
            <div className="flex items-center gap-2">
              <RadioTower className="w-4 h-4 text-gold animate-pulse" strokeWidth={1.5} />
              <span className="font-numbers text-[10px] tracking-[0.3em] uppercase text-gold">
                Live Queue
              </span>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="font-numbers text-[9px] tracking-[0.3em] uppercase text-white/40">
                  Now Serving
                </div>
                <div className="font-numbers text-lg tabular-nums text-white mt-0.5" data-testid="live-now-serving">
                  {nowServing ? `#${nowServing.token_number}` : "—"}
                </div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-right">
                <div className="font-numbers text-[9px] tracking-[0.3em] uppercase text-white/40">
                  Waiting
                </div>
                <div className="font-numbers text-lg tabular-nums text-white mt-0.5" data-testid="live-waiting-count">
                  {waitingCount}
                </div>
              </div>
            </div>
          </div>

          {/* Token */}
          <div className="mt-6 glass-strong rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">
              Your Token
            </div>
            <div className="font-numbers font-semibold text-[clamp(5rem,22vw,9rem)] leading-none text-gold gold-text-glow mt-2 tabular-nums">
              {me.token_number}
            </div>
            <div className="mt-4 hairline" />
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-numbers">
                  Customer
                </div>
                <div className="mt-1 font-heading text-lg">{me.customer_name}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-numbers">
                  Service
                </div>
                <div className="mt-1 font-body text-base text-white/80">
                  {me.service_name || "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Bento */}
          <div className="mt-5 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-charcoal border border-white/10 p-5">
              <div className="flex items-center gap-2 text-white/40 text-[10px] font-numbers tracking-[0.3em] uppercase">
                <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Position
              </div>
              <div className="mt-3 font-numbers text-3xl tabular-nums">
                {positionLabel()}
              </div>
              <div className="text-xs text-white/40 mt-1">
                {positionHint()}
              </div>
            </div>
            <div className="rounded-2xl bg-charcoal border border-white/10 p-5">
              <div className="flex items-center gap-2 text-white/40 text-[10px] font-numbers tracking-[0.3em] uppercase">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> ETA
              </div>
              <div className="mt-3 font-numbers text-3xl tabular-nums text-gold">
                {etaLabel()}
              </div>
              <div className="text-xs text-white/40 mt-1">approx.</div>
            </div>
          </div>

          {/* Live status banner */}
          <div className="mt-5 rounded-2xl border border-gold/20 bg-gold/5 p-5 flex items-center gap-3">
            {me.status === "completed" ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
            ) : (
              <RadioTower className="w-5 h-5 text-gold animate-pulse" strokeWidth={1.5} />
            )}
            <div className="text-sm text-white/70">
              {me.status === "serving" && "Please proceed to the counter."}
              {me.status === "waiting" && "Live updates — keep this page open."}
              {me.status === "completed" && "Thank you for visiting."}
              {me.status === "skipped" && "Your token was skipped. Please contact reception."}
            </div>
          </div>

          {/* Download ticket */}
          <button
            onClick={() => downloadTicketPng(shop, me)}
            data-testid="download-ticket"
            className="mt-5 w-full rounded-xl border border-gold/40 hover:bg-gold/10 transition-all px-4 py-3 flex items-center justify-center gap-2 text-sm text-gold font-numbers tracking-[0.25em] uppercase"
          >
            <Download className="w-4 h-4" /> Download My Ticket
          </button>

          <div className="mt-8 text-center text-[10px] font-numbers tracking-[0.3em] uppercase text-white/30">
            Live · Powered by AIBox Global
          </div>
        </motion.div>
      </main>
    </div>
  );
}
